import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  payrollPayments,
  PAYOUT_KIND_LABELS,
  type PayrollPaymentItem,
  type PayoutMethod,
  type PayoutKind,
} from '@/api/payrolls';
import { orgs } from '@/api/catalogs';
import { refs, type PayrollAccountsPreviewBatchRow } from '@/api/refs';
import { useConfirm } from '@/components/ConfirmProvider';
import { useNumberPreview } from '@/hooks/useNumberPreview';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import { useFormValidation } from '@/hooks/useFormValidation';
import DocumentToolbar from '@/shared/components/DocumentToolbar';
import DatePopover from '@/components/DatePopover';

interface FormState {
  organization_id: string;
  document_number: string;
  document_date: string;
  payroll_id: string;
  payment_method: PayoutMethod;  // спосіб-дефолт для рядків
  payout_kind: PayoutKind | '';  // '' = ще не обрано («вибрати»)
  bank_account_id: string;  // рахунок організації для безготівкових рядків (дефолт Основний)
  period_year: number;
  period_month: number;
  comment: string;
  items: PayrollPaymentItem[];
}

const MONTHS = ['', 'Січень', 'Лютий', 'Березень', 'Квітень', 'Травень', 'Червень',
  'Липень', 'Серпень', 'Вересень', 'Жовтень', 'Листопад', 'Грудень'];

const now = new Date();
const EMPTY_FORM: FormState = {
  organization_id: '',
  document_number: '',
  document_date: now.toISOString().slice(0, 16),
  payroll_id: '',
  payment_method: 'CASH',
  payout_kind: '',
  bank_account_id: '',
  period_year: now.getFullYear(),
  period_month: now.getMonth() + 1,
  comment: '',
  items: [],
};

const fmt = (n: number) =>
  n.toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function PayrollPaymentEditor() {
  const confirm = useConfirm();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { id } = useParams();
  const isNew = !id || id === 'new';
  // Предзаповнення зі сторінки «Борги по ЗП» (кнопка «оплатити» на документі):
  // ?org=&kind=&year=&month=&doc= → org/вид/період + автовибір документа.
  const [searchParams] = useSearchParams();
  const prefillOrg = searchParams.get('org');
  const prefillKind = searchParams.get('kind') as PayoutKind | null;
  const prefillDoc = searchParams.get('doc');
  const [prefillDocApplied, setPrefillDocApplied] = useState(false);

  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  // UX β: фільтр org dropdown по payroll.manage permission
  const {
    availableOrgs,
    hasPermissionInAnyOrg,
    totalAccessibleOrgs,
    loading: permsLoading,
  } = usePermissionsByOrg('payroll.manage');
  const docQuery = useQuery({
    queryKey: ['payroll-payment', id],
    queryFn: () => payrollPayments.get(id!),
    enabled: !isNew,
  });

  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const numberPreview = useNumberPreview('doc.payroll_payments', form.organization_id, isNew);
  const v = useFormValidation();

  function onSave() {
    if (!v.check([
      ['organization_id', form.organization_id, 'Оберіть організацію'],
      ['document_date', form.document_date, 'Вкажіть дату'],
      ['payout_kind', form.payout_kind, 'Оберіть вид виплати'],
    ])) return;
    isNew ? create.mutate() : update.mutate();
  }
  function onSaveAndPost() {
    if (!v.check([
      ['organization_id', form.organization_id, 'Оберіть організацію'],
      ['document_date', form.document_date, 'Вкажіть дату'],
      ['payout_kind', form.payout_kind, 'Оберіть вид виплати'],
    ])) return;
    createAndPost.mutate();
  }
  function onUpdateAndPost() {
    if (!v.check([
      ['organization_id', form.organization_id, 'Оберіть організацію'],
      ['document_date', form.document_date, 'Вкажіть дату'],
      ['payout_kind', form.payout_kind, 'Оберіть вид виплати'],
    ])) return;
    updateAndPost.mutate();
  }
  const [error, setError] = useState<string | null>(null);

  const empQ = useQuery({
    queryKey: ['employees', form.organization_id],
    queryFn: () => refs.employees(form.organization_id),
    enabled: !!form.organization_id,
  });

  // Банк-рахунки організації (для вибору рахунку безготівкових виплат).
  const bankAccQ = useQuery({
    queryKey: ['bank-accounts', form.organization_id],
    queryFn: () => refs.bankAccounts(form.organization_id),
    enabled: !!form.organization_id,
  });
  // Дефолт — Основний (is_main) рахунок, коли ще не обрано.
  useEffect(() => {
    if (!form.bank_account_id && bankAccQ.data && bankAccQ.data.length > 0) {
      const main = bankAccQ.data.find((a) => a.is_main) ?? bankAccQ.data[0];
      if (main) setForm((f) => f.bank_account_id ? f : { ...f, bank_account_id: main.id });
    }
  }, [bankAccQ.data]);

  // «На основі нарахування»: проведені НЕОПЛАЧЕНІ документи обраного виду+періоду.
  // Залежить від org + вид + період (зміна будь-чого → перезавантаження списку).
  const unpaidDocsQ = useQuery({
    queryKey: ['unpaid-docs', form.organization_id, form.payout_kind, form.period_year, form.period_month],
    queryFn: () => payrollPayments.unpaidDocuments({
      organization_id: form.organization_id, payout_kind: form.payout_kind as PayoutKind,
      period_year: form.period_year, period_month: form.period_month,
    }),
    enabled: !!form.organization_id && !!form.payout_kind && isNew,
  });

  // Закладка «Рахунки обліку»: ефективний рах. розрахунків per-рядок (той самий
  // резолвер, що проведення) + override у рядку (settlement_account_code).
  const [docTab, setDocTab] = useState<'calc' | 'accounts'>('calc');
  const accountsPreviewKind =
    form.payout_kind === 'VACATION' ? 'VACATION'
      : (form.payout_kind === 'SICK_ORG' || form.payout_kind === 'SICK_PFU') ? 'SICK'
      : form.payout_kind === 'ADVANCE' ? 'ADVANCE' : 'SALARY';
  const accEmpIds = form.items.filter((it) => it.employee_id).map((it) => it.employee_id);
  const accountsQ = useQuery({
    queryKey: ['payment-accounts-preview', form.organization_id, accountsPreviewKind, accEmpIds.join(',')],
    queryFn: () => refs.payrollAccountRules.previewBatch({
      organization_id: form.organization_id,
      employee_ids: accEmpIds,
      payment_kind: accountsPreviewKind,
    }),
    enabled: docTab === 'accounts' && !!form.organization_id && accEmpIds.length > 0,
  });
  const accByEmp = useMemo(() => {
    const m = new Map<string, PayrollAccountsPreviewBatchRow>();
    accountsQ.data?.rows.forEach((r) => m.set(r.employee_id, r));
    return m;
  }, [accountsQ.data]);

  useEffect(() => {
    if (isNew) {
      if (prefillOrg && prefillKind) {
        const y = Number(searchParams.get('year') || '');
        const m = Number(searchParams.get('month') || '');
        setForm((f) => f.organization_id ? f : {
          ...f, organization_id: prefillOrg, payout_kind: prefillKind,
          period_year: y || f.period_year, period_month: m || f.period_month,
        });
        return;
      }
      const firstOrg = availableOrgs[0]?.id ?? '';
      setForm((f) => f.organization_id ? f : { ...f, organization_id: firstOrg });
    } else if (docQuery.data) {
      const d = docQuery.data;
      setForm({
        organization_id: d.organization_id,
        document_number: d.document_number,
        document_date: d.document_date.slice(0, 16),
        payroll_id: d.payroll_id ?? '',
        payment_method: (d.payment_method as PayoutMethod) ?? 'CASH',
        payout_kind: ((d.payout_kind as string) === 'SALARY' ? 'MAIN' : d.payout_kind as PayoutKind) ?? 'MAIN',
        // рахунок з BANK-рядка (усі однакові) або порожньо.
        bank_account_id: d.items.find((it) => it.bank_account_id)?.bank_account_id ?? '',
        period_year: d.period_year ?? now.getFullYear(),
        period_month: d.period_month ?? (now.getMonth() + 1),
        comment: d.comment ?? '',
        items: d.items.map((it) => ({
          line_number: it.line_number,
          employee_id: it.employee_id,
          amount: String(it.amount),
          payment_method: it.payment_method ?? null,
          cash_register_id: it.cash_register_id ?? null,
          bank_account_id: it.bank_account_id ?? null,
          recipient_iban: it.recipient_iban ?? null,
          recipient_bank_name: it.recipient_bank_name ?? null,
          settlement_account_code: it.settlement_account_code ?? null,
        })),
      });
    }
  }, [isNew, docQuery.data, availableOrgs]);

  // Автовибір документа з «Боргів по ЗП»: коли список «На основі» завантажився —
  // обрати переданий документ (заповнить рядки залишками боргу).
  useEffect(() => {
    if (!isNew || !prefillDoc || prefillDocApplied || !unpaidDocsQ.data) return;
    setPrefillDocApplied(true);
    fillFromDocument(prefillDoc);
  }, [isNew, prefillDoc, prefillDocApplied, unpaidDocsQ.data]);

  const isPosted = !isNew && (docQuery.data?.posted ?? false);
  const total = form.items.reduce((s, e) => s + Number(e.amount || 0), 0);

  // Вибір документа «На основі»: рядки = його items (employee + сума до виплати).
  // Спосіб per-рядок = suggested (IBAN→BANK, інакше CASH); борг-підказка = accrued.
  function fillFromDocument(docId: string) {
    const doc = unpaidDocsQ.data?.documents.find((d) => d.id === docId);
    if (!doc) {
      setForm((f) => ({ ...f, payroll_id: docId, items: [] }));
      return;
    }
    setForm((f) => ({
      ...f,
      payroll_id: docId,
      items: doc.items.map((it, idx) => ({
        line_number: idx + 1,
        employee_id: it.employee_id,
        amount: it.amount,
        payment_method: it.suggested_method,  // каса/банк за наявністю IBAN
        accrued: it.amount,
      })),
    }));
  }



  const buildPayload = () => ({
    organization_id: form.organization_id,
    document_number: form.document_number,
    document_date: new Date(form.document_date).toISOString(),
    // payroll_id — FK на doc.payrolls. Несемо ЛИШЕ для MAIN/ADVANCE (документ реально
    // з payrolls). Для VACATION/SICK/COMPENSATION id — з інших таблиць → null (інакше FK).
    payroll_id: (form.payout_kind === 'MAIN' || form.payout_kind === 'ADVANCE'
                  || form.payout_kind === 'CIVIL')
      ? (form.payroll_id || null) : null,
    payment_method: form.payment_method,
    payout_kind: form.payout_kind as PayoutKind,  // непорожній (валідація onSave)
    period_year: form.period_year,
    period_month: form.period_month,
    comment: form.comment || null,
    // accrued — лише фронт-підказка, прибираємо перед відправкою (backend extra=forbid).
    // BANK-рядкам проставляємо обраний рахунок організації (якщо у рядку не заданий свій).
    items: form.items.map(({ accrued: _a, ...it }) => ({
      ...it,
      bank_account_id: it.payment_method === 'BANK'
        ? (it.bank_account_id || form.bank_account_id || null)
        : (it.bank_account_id ?? null),
    })),
  });

  const create = useMutation({
    mutationFn: () => payrollPayments.create(buildPayload()),
    onSuccess: (doc) => {
      qc.invalidateQueries({ queryKey: ['payroll-payments'] });
      qc.setQueryData(['payroll-payment', doc.id], doc);  // кеш ДО navigate (без гонки)
      navigate(`/payroll-payments/${doc.id}`);
    },
    onError: (e) => setError(extractError(e)),
  });
  const update = useMutation({
    mutationFn: () => payrollPayments.update(id!, buildPayload()),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['payroll-payment', id] }),
    onError: (e) => setError(extractError(e)),
  });
  const createAndPost = useMutation({
    mutationFn: async () => {
      const doc = await payrollPayments.create(buildPayload());
      await payrollPayments.post(doc.id);
      // Забрати СВIЖИЙ проведений документ (posted=true + payment_docs) і покласти
      // у кеш ДО navigate — інакше гонка: docQuery ще undefined → форма показує
      // непроведений create-doc, блок кас «не знайдено», isPosted=false.
      const fresh = await payrollPayments.get(doc.id);
      qc.setQueryData(['payroll-payment', doc.id], fresh);
      return fresh;
    },
    onSuccess: (fresh) => {
      qc.invalidateQueries({ queryKey: ['payroll-payments'] });
      navigate(`/payroll-payments/${fresh.id}`);
    },
    onError: (e) => setError(extractError(e)),
  });
  const updateAndPost = useMutation({
    mutationFn: async () => {
      await payrollPayments.update(id!, buildPayload());
      await payrollPayments.post(id!);
      // Забрати СВIЖИЙ проведений документ і покласти у кеш СИНХРОННО — інакше
      // invalidate дає рефетч-гонку: форма показує непроведений стан + блок кас
      // «немає документа» до завершення рефетчу (той самий баг, що у createAndPost).
      const fresh = await payrollPayments.get(id!);
      qc.setQueryData(['payroll-payment', id], fresh);
      return fresh;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['payroll-payments'] });
    },
    onError: (e) => setError(extractError(e)),
  });
  const unpost = useMutation({
    mutationFn: async () => {
      const fresh = await payrollPayments.unpost(id!);
      qc.setQueryData(['payroll-payment', id], fresh);
      return fresh;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['payroll-payments'] });
    },
    onError: (e) => setError(extractError(e)),
  });
  const remove = useMutation({
    mutationFn: () => payrollPayments.remove(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['payroll-payments'] });
      navigate('/payroll-payments');
    },
  });

  function setItem(i: number, patch: Partial<PayrollPaymentItem>) {
    setForm((f) => ({
      ...f,
      items: f.items.map((e, idx) => idx === i ? { ...e, ...patch } : e),
    }));
  }
  function addRow() {
    setForm((f) => ({
      ...f,
      items: [...f.items, {
        line_number: f.items.length + 1,
        employee_id: '', amount: '0',
      }],
    }));
  }
  function removeRow(i: number) {
    setForm((f) => ({
      ...f,
      items: f.items.filter((_, idx) => idx !== i)
        .map((e, idx) => ({ ...e, line_number: idx + 1 })),
    }));
  }

  const employeeName = useMemo(() => {
    const m = new Map<string, string>();
    empQ.data?.forEach((e) => m.set(e.id, e.code ? `${e.code} — ${e.name}` : e.name));
    return m;
  }, [empQ.data]);

  // UX β: warning якщо новий документ а user не має payroll.manage у жодній org
  if (isNew && !permsLoading && !hasPermissionInAnyOrg) {
    return (
      <div className="card">
        <div style={{ padding: '1.5rem', textAlign: 'center' }}>
          <div style={{
            padding: '1rem', background: '#fef3c7', borderRadius: 6,
            border: '1px solid #fbbf24', color: '#92400e',
            maxWidth: 600, margin: '1rem auto',
          }}>
            ⚠ У вас немає прав створювати виплату зарплати у жодній з ваших
            організацій ({totalAccessibleOrgs}). Зверніться до
            адміністратора tenant'а.
          </div>
          <button onClick={() => navigate('/payroll-payments')}>Скасувати</button>
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>
          {isNew ? 'Нова виплата ЗП' : `Виплата № ${form.document_number}`}
          {isPosted && (
            <span style={{ marginLeft: '0.75rem', color: '#15803d', fontSize: '1rem' }}>
              ✓ проведено
            </span>
          )}
        </h2>
        <DocumentToolbar
          onBackToList={() => navigate('/payroll-payments')}
          onSave={onSave}
          saveDisabled={create.isPending || update.isPending}
          isPosted={isPosted}
          isNew={isNew}
          onPost={isNew ? onSaveAndPost : onUpdateAndPost}
          postDisabled={isNew
            ? (create.isPending || createAndPost.isPending)
            : (form.items.length === 0 || updateAndPost.isPending || update.isPending)}
          postTitle="Зберігає поточні зміни та проводить документ"
          onUnpost={() => unpost.mutate()}
          unpostPending={unpost.isPending}
          onDelete={() => { confirm('Видалити документ?').then((ok) => { if (ok) remove.mutate(); }) }}
          deletePending={remove.isPending}
          onPrint={() => navigate(`/payroll-payments/${id}/print`)}
        />
      </div>

      {v.banner()}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '0.75rem' }}>
        <div className="field">
          <label>Організація</label>
          <select value={form.organization_id}
                  onChange={(e) => setForm({ ...form, organization_id: e.target.value })}
                  disabled={!isNew}>
            <option value="">—</option>
            {(isNew ? availableOrgs : orgList.data?.items ?? []).map((o) => (
              <option key={o.id} value={o.id}>{o.code} — {o.name}</option>
            ))}
          </select>
          {isNew && availableOrgs.length < totalAccessibleOrgs && (
            <small style={{ color: '#64748b', fontSize: '0.75rem' }}>
              Показано {availableOrgs.length} з {totalAccessibleOrgs} ваших організацій
              (інші не дозволяють цю операцію)
            </small>
          )}
        </div>
        <div className="field">
          <label>№ документа</label>
          <input value={form.document_number}
                 onChange={(e) => setForm({ ...form, document_number: e.target.value })}
                 placeholder={isNew && numberPreview ? `буде: ${numberPreview}` : ''}
                 disabled={isPosted} />
        </div>
        <div className="field">
          <label>Дата</label>
          <DatePopover withTime value={form.document_date}
                 onChange={(val) => setForm({ ...form, document_date: val })}
                 disabled={isPosted} />
        </div>
        <div className="field">
          <label>Вид виплати</label>
          <select value={form.payout_kind}
                  onChange={(e) => setForm({
                    ...form,
                    payout_kind: e.target.value as PayoutKind | '',
                    // Зміна виду → скидаємо період (на поточний), документ і рядки.
                    period_year: now.getFullYear(),
                    period_month: now.getMonth() + 1,
                    payroll_id: '',
                    items: [],
                  })}
                  disabled={isPosted}>
            <option value="">— вибрати —</option>
            {(Object.entries(PAYOUT_KIND_LABELS) as Array<[PayoutKind, string]>)
              .map(([k, lbl]) => <option key={k} value={k}>{lbl}</option>)}
          </select>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '0.75rem' }}>
        <div className="field">
          <label>Період</label>
          <div style={{ display: 'flex', gap: 4 }}>
            <select value={form.period_month}
                    onChange={(e) => setForm({ ...form, period_month: Number(e.target.value), payroll_id: '', items: [] })}
                    disabled={isPosted || !form.payout_kind} style={{ flex: 2 }}>
              {MONTHS.slice(1).map((m, i) => (
                <option key={i + 1} value={i + 1}>{m}</option>
              ))}
            </select>
            <select value={form.period_year}
                    onChange={(e) => setForm({ ...form, period_year: Number(e.target.value), payroll_id: '', items: [] })}
                    disabled={isPosted || !form.payout_kind} style={{ flex: 1 }}>
              {Array.from({ length: 7 }, (_, i) => now.getFullYear() - 5 + i).map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
        </div>
        <div className="field">
          <label>На основі нарахування (проведені неоплачені за період)</label>
          <select value={form.payroll_id}
                  onChange={(e) => fillFromDocument(e.target.value)}
                  disabled={isPosted || !form.payout_kind}>
            <option value="">
              {!form.payout_kind ? '— спершу оберіть вид виплати —'
                : unpaidDocsQ.isFetching ? '— завантаження…'
                : (unpaidDocsQ.data?.documents.length ?? 0) === 0
                  ? '— немає неоплачених документів цього виду за період —'
                  : '— оберіть документ —'}
            </option>
            {unpaidDocsQ.data?.documents.map((d) => (
              <option key={d.id} value={d.id}>
                {d.document_number}
                {d.employee_name ? ` — ${d.employee_name}` : ` — ${d.employee_count} осіб`}
                {' '}({Number(d.total).toFixed(2)} грн до виплати)
              </option>
            ))}
          </select>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', alignItems: 'end' }}>
        <div className="field">
          <label>Рахунок організації (для безготівкових рядків)</label>
          <select value={form.bank_account_id}
                  onChange={(e) => setForm({ ...form, bank_account_id: e.target.value })}
                  disabled={isPosted}>
            <option value="">— оберіть рахунок —</option>
            {bankAccQ.data?.map((a) => (
              <option key={a.id} value={a.id}>
                {a.is_main ? '★ Основний — ' : ''}{a.account_name || a.iban}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          {/* Податкові виплати (ПДФО/ВЗ/ЄСВ) — активна лише після збереження виплати
              (суми податків тягнуться з пов'язаного нарахування/боргу бюджету). */}
          <button
            disabled={isNew || !docQuery.data}
            title={(isNew || !docQuery.data)
              ? 'Спочатку збережіть виплату — тоді можна створити податкові виплати'
              : 'Створити перерахування ПДФО/ВЗ/ЄСВ на основі цієї виплати'}
            onClick={() => navigate(`/tax-remittances/new?from_payment=${id}`)}>
            створити податкові виплати
          </button>
        </div>
      </div>

      {form.payout_kind === 'SICK_PFU' && (
        <p style={{ marginTop: '0.5rem', padding: '0.6rem 0.8rem', background: '#fef3c7',
                    border: '1px solid #f59e0b', borderRadius: 6,
                    color: '#b45309', fontSize: '0.85rem' }}>
          ⚠ Лікарняний за рахунок Фонду виплачується зі <strong>спецрахунку ПФУ</strong> —
          оберіть саме його у полі «Рахунок організації» вище (не основний р/р).
        </p>
      )}

      <div style={{ marginTop: '1rem', display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
        <h3 style={{ margin: 0 }}>Виплати по співробітниках</h3>
        {/* Закладки: рядки виплати ↔ рахунки обліку (рішення власника 2026-07-07). */}
        <div style={{ display: 'flex', border: '1px solid var(--border)', borderRadius: 6, overflow: 'hidden' }}>
          {([['calc', 'Розрахунок'], ['accounts', 'Рахунки обліку']] as const).map(([t, l]) => (
            <button key={t} onClick={() => setDocTab(t)}
              style={{ padding: '0.25rem 0.8rem', border: 'none', cursor: 'pointer', fontSize: '0.78rem',
                background: docTab === t ? 'var(--primary)' : 'transparent',
                color: docTab === t ? '#fff' : 'var(--text-secondary)' }}>
              {l}
            </button>
          ))}
        </div>
      </div>

      {docTab === 'accounts' && (
        <div>
          <p style={{ color: 'var(--muted)', fontSize: '0.8rem', margin: '0.5rem 0' }}>
            Рах. розрахунків рядка: порожньо — автоматичний (правило обліку /
            персональний / ЦПУ 6851 / стандартний); введене значення — застосується
            саме воно у проводці та погашенні боргу.
          </p>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
            <thead>
              <tr style={{ background: '#f8fafc', textAlign: 'left' }}>
                <th style={{ padding: '0.4rem 0.5rem' }}>Співробітник</th>
                <th style={{ padding: '0.4rem 0.5rem', width: 130, textAlign: 'right' }}>Сума</th>
                <th style={{ padding: '0.4rem 0.5rem', width: 200 }}>Рах. розрахунків (Дт)</th>
                <th style={{ padding: '0.4rem 0.5rem', width: 140 }}>Кт (спосіб)</th>
              </tr>
            </thead>
            <tbody>
              {form.items.filter((it) => it.employee_id).map((it, i) => {
                const prev = accByEmp.get(it.employee_id);
                const auto = prev?.payroll.account_code ?? '…';
                const src = prev?.payroll.source;
                const method = it.payment_method ?? form.payment_method;
                return (
                  <tr key={i} style={{ borderTop: '1px solid #e2e8f0' }}>
                    <td style={{ padding: '0.3rem 0.5rem' }}>{employeeName.get(it.employee_id) ?? '—'}</td>
                    <td style={{ padding: '0.3rem 0.5rem', textAlign: 'right' }}>{fmt(Number(it.amount || 0))}</td>
                    <td style={{ padding: '0.3rem 0.5rem' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <input value={it.settlement_account_code ?? ''} disabled={isPosted}
                          placeholder={auto}
                          style={{ width: 90 }}
                          onChange={(e) => setItem(form.items.indexOf(it), {
                            settlement_account_code: e.target.value || null,
                          })} />
                        {/* «інд» — лише де рахунок НЕ з документа (правило/картка/ЦПУ). */}
                        {!it.settlement_account_code && src
                          && ['rule', 'card', 'civil_default'].includes(src) && (
                          <span style={{ fontSize: '0.7rem', color: '#94a3b8' }}
                            title={src === 'rule' ? 'правило обліку'
                              : src === 'card' ? 'персональний рахунок працівника'
                              : 'ЦПУ-дефолт'}>
                            інд
                          </span>
                        )}
                      </div>
                    </td>
                    <td style={{ padding: '0.3rem 0.5rem' }}>
                      {method === 'BANK' ? '311 (банк)' : '301 (каса)'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {accEmpIds.length === 0 && (
            <p style={{ color: 'var(--muted)', fontSize: '0.85rem' }}>
              Заповніть рядки на закладці «Розрахунок».
            </p>
          )}
        </div>
      )}

      <div style={{ display: docTab === 'calc' ? 'block' : 'none' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
        <thead>
          <tr style={{ background: '#f8fafc', textAlign: 'left' }}>
            <th style={{ padding: '0.5rem', width: 30 }}>#</th>
            <th style={{ padding: '0.5rem' }}>Співробітник</th>
            <th style={{ padding: '0.5rem', width: 120 }}>Спосіб</th>
            <th style={{ padding: '0.5rem', width: 150 }}>Сума виплати</th>
            <th style={{ padding: '0.5rem', width: 40 }}></th>
          </tr>
        </thead>
        <tbody>
          {form.items.map((it, i) => (
            <tr key={i} style={{ borderTop: '1px solid #e2e8f0' }}>
              <td style={{ padding: '0.5rem' }}>{it.line_number}</td>
              <td style={{ padding: '0.5rem' }}>
                <select value={it.employee_id}
                        onChange={(e) => setItem(i, { employee_id: e.target.value })}
                        disabled={isPosted}
                        style={{ width: '100%' }}>
                  <option value="">—</option>
                  {empQ.data?.map((e) => (
                    <option key={e.id} value={e.id}>
                      {employeeName.get(e.id)}
                    </option>
                  ))}
                </select>
              </td>
              <td style={{ padding: '0.5rem' }}>
                {/* Спосіб per-рядок. Порожньо/CASH = каса (дефолт для тих, у кого
                    немає банк-рахунку). Підтягування ставить BANK тим, у кого є IBAN. */}
                <select value={it.payment_method ?? 'CASH'}
                        onChange={(e) => setItem(i, {
                          payment_method: e.target.value as PayoutMethod,
                        })}
                        disabled={isPosted} style={{ width: '100%' }}>
                  <option value="CASH">Каса</option>
                  <option value="BANK">Банк</option>
                </select>
              </td>
              <td style={{ padding: '0.5rem' }}>
                <input type="number" step="0.01" value={it.amount}
                       onChange={(e) => setItem(i, { amount: e.target.value })}
                       disabled={isPosted}
                       style={{ textAlign: 'right', width: '100%' }} />
                {it.accrued && Number(it.accrued) - Number(it.amount || 0) > 0.005 && (
                  <div style={{ fontSize: '0.72rem', color: '#b45309', textAlign: 'right' }}
                       title="Залишок неоплаченого піде у борг (підтягнеться через «Нарахування (борг)»)">
                    у борг: {(Number(it.accrued) - Number(it.amount || 0)).toFixed(2)}
                  </div>
                )}
              </td>
              <td style={{ padding: '0.5rem' }}>
                {!isPosted && (
                  <button onClick={() => removeRow(i)} className="danger"
                          style={{ padding: '0.3rem 0.6rem' }}>×</button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr style={{ borderTop: '2px solid #cbd5e1', fontWeight: 600 }}>
            <td colSpan={3} style={{ padding: '0.5rem', textAlign: 'right' }}>Усього:</td>
            <td style={{ padding: '0.5rem', textAlign: 'right' }}>{fmt(total)}</td>
            <td></td>
          </tr>
        </tfoot>
      </table>
      </div>
      {!isPosted && (
        <button onClick={addRow} style={{ marginTop: '0.75rem' }}>+ Додати рядок</button>
      )}

      <div className="field" style={{ marginTop: '1rem' }}>
        <label>Коментар</label>
        <input value={form.comment}
               onChange={(e) => setForm({ ...form, comment: e.target.value })}
               disabled={isPosted} />
      </div>

      {error && <div className="error">{error}</div>}

      {!isPosted && (
        <p style={{ marginTop: '1rem', padding: '0.6rem 0.8rem', background: '#eff6ff',
                    border: '1px solid #bfdbfe', borderRadius: 6,
                    color: '#1e3a8a', fontSize: '0.85rem' }}>
          ℹ <strong>При проведенні</strong> автоматично створяться і проведуться
          платіжні документи: один <strong>РКО</strong> на касу (рядки «Готівка») та
          один <strong>банк-платіж</strong> на рахунок (рядки «Банк»). Проводки
          <strong> Дт 661 — Кт 301/311</strong> робляться по кожному співробітнику.
        </p>
      )}

      {isPosted && docQuery.data?.payment_docs && docQuery.data.payment_docs.length > 0 && (
        <div style={{ marginTop: '1rem', padding: '0.6rem 0.8rem', background: '#f0fdf4',
                      border: '1px solid #bbf7d0', borderRadius: 6, fontSize: '0.85rem' }}>
          <strong style={{ color: '#15803d' }}>Створені платіжні документи:</strong>
          <ul style={{ margin: '0.4rem 0 0', paddingLeft: '1.2rem' }}>
            {docQuery.data.payment_docs.map((pd) => (
              <li key={pd.doc_id}>
                {pd.doc_type === 'cash' ? '🧾 РКО (готівка)' : '🏦 Банк-платіж'}
                {' — '}
                <button
                  style={{ padding: '0.1rem 0.4rem', fontSize: '0.78rem' }}
                  onClick={() => navigate(
                    pd.doc_type === 'cash'
                      ? `/cash-payments/${pd.doc_id}`
                      : `/bank-payments/${pd.doc_id}`,
                  )}
                >відкрити</button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function extractError(e: unknown): string {
  if (typeof e === 'object' && e && 'response' in e) {
    const r = (e as { response?: { data?: { detail?: string } } }).response;
    return r?.data?.detail ?? 'Error';
  }
  return String(e);
}
